import type {
  AppointmentReportSource,
  BusinessReport,
  PaymentReportMethod,
  ReportWindowInput,
} from '@borradh-workspace/api-client/types';
import { leadSourceLabels } from '@borradh-workspace/api-client/types';
import { CalendarDays, CreditCard, Receipt, Users } from 'lucide-react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { useGetBusinessReport } from '../api';
import {
  WEEKDAY_NAMES,
  countOf,
  describeChange,
  describeRateChange,
  formatCount,
  formatPercent,
} from '../lib/format';
import { DailyStackedBars, SERIES_COLOR } from './report-charts';
import {
  BarList,
  Figure,
  Legend,
  Panel,
  ReportSection,
  SERIES,
  SplitBar,
  StatCard,
} from './report-primitives';
import { ReportState } from './report-state';

const SOURCE_LABELS: Record<AppointmentReportSource, string> = {
  booking_page: 'Booking page',
  claire_chat: 'Claire · messages',
  claire_phone: 'Claire · phone',
  team: 'Added by your team',
  calendar_sync: 'Synced calendar',
};

const METHOD_LABELS: Record<PaymentReportMethod, string> = {
  card_terminal: 'Card terminal',
  saved_card: 'Saved card',
  qr_self_checkout: 'QR self-checkout',
  manual_card: 'Card entered by hand',
  cash: 'Cash',
  gift_card: 'Gift card',
  online_deposit: 'Online deposit',
};

const pct = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

export function BusinessReportView({ range }: { range: ReportWindowInput }) {
  const { report, isLoading, isError, refetch } = useGetBusinessReport(range);
  return (
    <ReportState
      isLoading={isLoading}
      isError={isError}
      onRetry={() => refetch()}
    >
      {report ? <BusinessReportBody report={report} /> : null}
    </ReportState>
  );
}

function BusinessReportBody({ report }: { report: BusinessReport }) {
  const { headline, appointments, sales, clients, payments } = report;
  const days = report.appointments.byDay.length;

  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))] gap-4">
        <StatCard
          label="Appointments"
          value={formatCount(headline.appointments.value)}
          change={describeChange(
            headline.appointments.value,
            headline.appointments.previous
          )}
          footnote={`${formatCount(headline.appointments.previous)} in the previous ${days} days`}
        />
        <StatCard
          label="Clients seen"
          value={formatCount(headline.clientsSeen.value)}
          change={describeChange(
            headline.clientsSeen.value,
            headline.clientsSeen.previous
          )}
          footnote={`${formatCount(clients.new)} new · ${formatCount(clients.returning)} returning`}
        />
        <StatCard
          label="New clients"
          value={formatCount(headline.newClients.value)}
          change={describeChange(
            headline.newClients.value,
            headline.newClients.previous
          )}
          footnote="First ever visit in this period"
        />
        <StatCard
          label="No-show rate"
          value={formatPercent(headline.noShowRate.value, 1)}
          change={describeRateChange(
            headline.noShowRate.value,
            headline.noShowRate.previous
          )}
          goodWhen="down"
          footnote={countOf(appointments.noShows, 'missed appointment')}
        />
      </div>

      <ReportSection
        id="appointments"
        icon={<CalendarDays />}
        title="Online Appointments"
        description="Everything booked through Borrah. Walk-ins aren't recorded yet."
      >
        <Panel>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(130px,1fr))] gap-5">
            <Figure label="Booked" value={formatCount(appointments.total)} />
            <Figure
              label="Attended"
              swatch={SERIES.primary}
              value={formatCount(appointments.attended)}
              hint={pct(appointments.attended, appointments.total)}
            />
            <Figure
              label="Not checked out"
              swatch={SERIES.muted}
              value={formatCount(appointments.notCheckedOut)}
              hint="Past, still marked booked"
            />
            <Figure
              label="Upcoming"
              swatch={SERIES.upcoming}
              value={formatCount(appointments.upcoming)}
            />
            <Figure
              label="Cancelled"
              swatch={SERIES.tertiary}
              value={formatCount(appointments.cancelled)}
              hint={pct(appointments.cancelled, appointments.total)}
            />
            <Figure
              label="No-shows"
              swatch={SERIES.secondary}
              value={formatCount(appointments.noShows)}
              hint={pct(appointments.noShows, appointments.total)}
            />
          </div>
          <SplitBar
            label="What happened to this period's appointments"
            legend={false}
            segments={[
              {
                label: 'Attended',
                value: appointments.attended,
                color: SERIES.primary,
              },
              {
                label: 'Not checked out',
                value: appointments.notCheckedOut,
                color: SERIES.muted,
              },
              {
                label: 'Upcoming',
                value: appointments.upcoming,
                color: SERIES.upcoming,
              },
              {
                label: 'Cancelled',
                value: appointments.cancelled,
                color: SERIES.tertiary,
              },
              {
                label: 'No-shows',
                value: appointments.noShows,
                color: SERIES.secondary,
              },
            ]}
          />
        </Panel>

        <Panel
          title="Appointments by day"
          description="By the day of the appointment, cancellations left out"
          action={
            <Legend
              items={[
                { label: 'Booked by clients', color: SERIES.primary },
                { label: 'Added by your team', color: SERIES.secondary },
              ]}
            />
          }
        >
          <DailyStackedBars
            label="Appointments by day"
            data={appointments.byDay}
            series={[
              {
                key: 'byClients',
                label: 'Booked by clients',
                color: SERIES_COLOR.primary,
              },
              {
                key: 'byTeam',
                label: 'Added by your team',
                color: SERIES_COLOR.secondary,
              },
            ]}
          />
        </Panel>

        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-4">
          <Panel title="By team member">
            {appointments.byTeamMember.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No appointments in this period.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-muted-foreground text-xs">
                  <tr className="text-left">
                    <th className="pb-2 font-medium">Team member</th>
                    <th className="pb-2 pl-3 text-right font-medium">
                      Appointments
                    </th>
                    <th className="pb-2 pl-3 text-right font-medium">
                      Attended
                    </th>
                    <th className="pb-2 pl-3 text-right font-medium">
                      No-shows
                    </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {appointments.byTeamMember.map((member) => (
                    <tr key={member.userId} className="border-t">
                      <td className="py-2.5 font-medium">{member.name}</td>
                      <td className="py-2.5 pl-3 text-right font-semibold">
                        {formatCount(member.total)}
                      </td>
                      <td className="py-2.5 pl-3 text-right text-muted-foreground">
                        {formatCount(member.attended)}
                      </td>
                      <td className="py-2.5 pl-3 text-right text-muted-foreground">
                        {formatCount(member.noShows)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
          <Panel title="Where bookings came from">
            <BarList
              empty="No bookings in this period."
              items={appointments.bySource.map((s) => ({
                key: s.source,
                label: SOURCE_LABELS[s.source],
                value: s.count,
              }))}
            />
            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <Figure
                label="Paid a deposit"
                value={pct(appointments.withPaidDeposit, appointments.total)}
                hint={countOf(appointments.withPaidDeposit, 'appointment')}
              />
              <Figure
                label="Booked ahead"
                value={
                  appointments.averageLeadDays === null
                    ? '—'
                    : `${appointments.averageLeadDays.toFixed(1)} days`
                }
                hint="On average"
              />
            </div>
          </Panel>
        </div>
      </ReportSection>

      <ReportSection
        id="sales"
        icon={<Receipt />}
        title="Sales"
        description="Completed checkouts: what sold and who sold it"
      >
        {sales.sales === 0 ? (
          <Panel>
            <p className="font-medium">
              No completed checkouts in this period.
            </p>
            <p className="text-muted-foreground text-sm">
              {sales.open > 0
                ? `${countOf(sales.open, 'checkout')} ${sales.open === 1 ? 'was' : 'were'} started but never completed. Complete them in Sales to see what sold here.`
                : 'Sales appear here once appointments are checked out in Borrah.'}
            </p>
          </Panel>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-4">
            <Panel title="Top sellers" className="lg:col-span-1">
              <Tabs defaultValue="treatments">
                <TabsList>
                  <TabsTrigger value="treatments">Treatments</TabsTrigger>
                  <TabsTrigger value="team">Team</TabsTrigger>
                  <TabsTrigger value="products">Products</TabsTrigger>
                </TabsList>
                <TabsContent value="treatments" className="pt-3">
                  <BarList
                    empty="No treatments sold."
                    items={sales.topTreatments.map((t) => ({
                      key: t.name,
                      label: t.name,
                      value: t.quantity,
                    }))}
                  />
                </TabsContent>
                <TabsContent value="team" className="pt-3">
                  <BarList
                    empty="No treatments sold."
                    items={sales.byTeamMember.map((t) => ({
                      key: t.name,
                      label: t.name,
                      value: t.quantity,
                    }))}
                  />
                </TabsContent>
                <TabsContent value="products" className="pt-3">
                  <BarList
                    empty="No products sold."
                    color={SERIES.secondary}
                    items={sales.topProducts.map((t) => ({
                      key: t.name,
                      label: t.name,
                      value: t.quantity,
                    }))}
                  />
                </TabsContent>
              </Tabs>
            </Panel>
            <Panel title="Sales mix">
              <SplitBar
                label="Items sold"
                segments={[
                  {
                    label: 'Treatments',
                    value: sales.treatmentsSold,
                    color: SERIES.primary,
                  },
                  {
                    label: 'Products',
                    value: sales.productsSold,
                    color: SERIES.secondary,
                  },
                ]}
              />
              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                <Figure label="Sales" value={formatCount(sales.sales)} />
                <Figure label="Refunded" value={formatCount(sales.refunded)} />
                <Figure
                  label="Busiest day"
                  value={
                    sales.busiestWeekday
                      ? WEEKDAY_NAMES[sales.busiestWeekday - 1]
                      : '—'
                  }
                />
                <Figure
                  label="Open checkouts"
                  value={formatCount(sales.open)}
                  hint="Started, not completed"
                />
              </div>
            </Panel>
          </div>
        )}
      </ReportSection>

      <ReportSection
        id="clients"
        icon={<Users />}
        title="Clients"
        description="Who came in, who was new and who's coming back"
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-4">
          <Panel title="New and returning">
            <div className="grid grid-cols-3 gap-4">
              <Figure
                label="Returning"
                swatch={SERIES.primary}
                value={formatCount(clients.returning)}
              />
              <Figure
                label="New"
                swatch={SERIES.secondary}
                value={formatCount(clients.new)}
              />
              <Figure label="Seen" value={formatCount(clients.seen)} />
            </div>
            <SplitBar
              label="Clients seen"
              legend={false}
              segments={[
                {
                  label: 'Returning',
                  value: clients.returning,
                  color: SERIES.primary,
                },
                { label: 'New', value: clients.new, color: SERIES.secondary },
              ]}
            />
            <div className="border-t pt-4">
              <Figure
                label="Booked their next visit"
                value={pct(clients.bookedAgain, clients.bookedAgainOutOf)}
                hint={`${formatCount(clients.bookedAgain)} of ${countOf(clients.bookedAgainOutOf, 'client')} whose visit has passed`}
              />
            </div>
          </Panel>
          <Panel title="Where new clients came from">
            <BarList
              empty="No new clients in this period."
              color={SERIES.secondary}
              items={clients.newBySource.map((s) => ({
                key: s.source ?? 'unknown',
                label: s.source ? leadSourceLabels[s.source] : 'Unknown',
                value: s.count,
              }))}
            />
          </Panel>
        </div>
      </ReportSection>

      <ReportSection
        id="payments"
        icon={<CreditCard />}
        title="Payments"
        description="How clients paid, deposits and refunds"
      >
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] gap-4">
          <Panel
            title="How clients paid"
            description={countOf(payments.total, 'payment')}
          >
            <BarList
              empty="No payments taken in this period."
              items={payments.byMethod.map((m) => ({
                key: m.method,
                label: METHOD_LABELS[m.method],
                value: m.count,
              }))}
            />
          </Panel>
          <Panel>
            <div className="grid grid-cols-2 gap-5">
              <Figure
                label="Deposits paid"
                value={formatCount(payments.depositsPaid)}
                hint="Online, before the visit"
              />
              <Figure
                label="Deposits kept"
                value={formatCount(payments.depositsKept)}
                hint="From no-shows and late cancellations"
              />
              <Figure label="Refunds" value={formatCount(payments.refunds)} />
              <Figure
                label="Sales with a tip"
                value={pct(payments.salesWithTip, sales.sales)}
                hint="Of completed sales"
              />
            </div>
          </Panel>
        </div>
      </ReportSection>
    </div>
  );
}
